// Per-bucket achievement subset — RP-7 of
// docs/planning/firefly_role_context_presentation.md (closes §7 L6).
//
// The dock's `AchievementsSection` renders for every audience (its
// `roleGate` is `"any-dock-audience"`), but the achievement catalog is
// heterogeneous — `ads_*` items only make sense to a sales rep who
// runs ads, `kb_*` items only make sense to a KB author, and so on.
// Pre-RP-7 every viewer saw every badge in their personal gallery; a
// manufacturing user with a "First ad logged" badge from a former
// sales hat saw it floating in their mfg-bucket dock with no context.
//
// This file defines the **bucket → achievement-categories** map and a
// pure filter that consumes it. Kept as a standalone pure helper
// (not a hook, not a React component) so:
//   - The map is the single source of truth — adding a new bucket
//     means adding one row here and one row in the lock test.
//   - The filter is testable in the Node-environment vitest setup
//     without dragging in jsdom / RTL (same doctrine as
//     `impersonationBanner.ts` from RP-5).
//   - When L2 promotes `bucketRelevance` from a declarative tag into
//     a hard render filter, this file is unaffected — it filters
//     *items inside a section*, not whether the section renders.
//
// Doctrine notes:
//   - `null` bucket (early bootstrap before mode resolves) ⇒ no
//     filtering. Safer to show too much than to flash an empty
//     gallery during the first paint.
//   - Unknown achievement keys (a future deploy ships a new badge
//     client hasn't seen yet) are KEPT. Forward-compat: a new badge
//     should appear in the user's gallery even if its category isn't
//     yet mapped on the client. The cost of a stray badge is much
//     lower than the cost of silently hiding a freshly-unlocked one.

import type { AchievementCategory } from "./achievementCatalog";
import { ACHIEVEMENT_CATALOG } from "./achievementCatalog";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";

/**
 * The locked bucket → achievement-categories mapping.
 *
 * Universal `"user"` everywhere: login / onboarding achievements are
 * bucket-agnostic — every user that opens the dock has logged in, and
 * the streak / first-login badges belong everywhere.
 *
 * Bucket-specific subsets (initial cut — extend as new categories
 * land and the planning doc's §5.x rows fill in):
 *   - `sales`: ads/sales/lead/training/user — today's effective set
 *     for a sales rep. `training` belongs here because the
 *     `training_*` catalog keys are rep-side practice actions
 *     (roleplay logged, KB question asked).
 *   - `manager`: kb/prompting/user — trainer / KB-author categories.
 *     `training` is intentionally NOT in this bucket: those badges are
 *     rep-side actions, not trainer-side authoring. A future "team
 *     coaching" surface (`firefly_visual_design.md` §RA-v3, owner-as-
 *     coach) would surface rep training data, but as a separate
 *     section, not by leaking rep badges into the manager dock view.
 *   - `admin` / `platform` / `manufacturing` / `production` /
 *     `fulfillment` / `ops` / `logistics`: `user` only. None of these
 *     buckets has a domain-specific achievement category in the
 *     catalog yet. When one lands (e.g. a `manufacturing` category
 *     for COGS close streaks, a `fulfillment` category for intake-form
 *     completion streaks per docs/planning/sales_fulfillment_bucket.md
 *     §6, or an `ops` category for deal-paperwork closure streaks), it
 *     slots into this map and the lock test fails until the matrix
 *     is updated in step.
 */
export const ACHIEVEMENT_CATEGORIES_BY_BUCKET: Record<
  BucketId,
  readonly AchievementCategory[]
> = {
  sales: ["ads", "sales", "lead", "training", "user"],
  dealer: ["sales", "user"],
  manager: ["kb", "prompting", "user"],
  coverage: ["user"],
  admin: ["user"],
  platform: ["user"],
  manufacturing: ["user"],
  production: ["user"],
  ops: ["user"],
  logistics: ["user"],
  fulfillment: ["user"],
  service: ["user"],
};

/**
 * Returns the achievement-category subset visible in `bucket`.
 *
 * `null` bucket ⇒ returns `null` (sentinel for "no filter"). Callers
 * should treat `null` as "show every category" rather than "show
 * nothing." This is the safe-fallback path for the early-bootstrap
 * frame where `currentMode` hasn't resolved yet.
 */
export function getAchievementCategoriesForBucket(
  bucket: BucketId | null,
): readonly AchievementCategory[] | null {
  if (bucket === null) return null;
  return ACHIEVEMENT_CATEGORIES_BY_BUCKET[bucket];
}

/**
 * Filters an achievement list down to the categories visible in
 * `bucket`. Pure; preserves input order; reference-equal on no-op
 * paths (null bucket) so React reconciliation stays cheap.
 *
 * Unknown achievement keys (not in `ACHIEVEMENT_CATALOG`) are KEPT —
 * see file header for the forward-compat rationale.
 *
 * Generic over the input shape: anything with an `achievement_key`
 * string field qualifies, so this works for both the wire shape
 * (`AdsActivityFreshAchievement`) and any future view-model wrapper.
 */
export function filterAchievementsForBucket<
  T extends { achievement_key: string },
>(achievements: readonly T[], bucket: BucketId | null): readonly T[] {
  const categories = getAchievementCategoriesForBucket(bucket);
  if (categories === null) return achievements;
  const allowed = new Set<AchievementCategory>(categories);
  return achievements.filter((a) => {
    const entry = ACHIEVEMENT_CATALOG[a.achievement_key];
    if (!entry) return true;
    return allowed.has(entry.category);
  });
}
